/**
 * Agent reference: terms used across ticket and refund flows.
 * Content mirrors server docs/AGENT_GLOSSARY.md (static copy for the admin panel).
 */
export default function AgentGlossaryModal({ open, onClose }) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="agent-glossary-title"
      onClick={onClose}
    >
      <div
        className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 mb-4 flex items-start justify-between gap-3 border-b border-gray-100 bg-white pb-3">
          <h2 id="agent-glossary-title" className="text-lg font-bold text-gray-900">
            Agent Glossary
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-3 py-1 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>

        <div className="space-y-5 text-sm text-gray-800">
          <p className="text-xs text-gray-500">
            Quick reference for terms you will see across the admin panel and ticket details.
          </p>

          <section>
            <h3 className="text-base font-semibold text-gray-900">Money and wallets</h3>
            <ul className="mt-2 list-disc space-y-2 pl-5">
              <li>
                <strong>Cash / Cash refund</strong> — Money sent back to the customer&apos;s original
                payment method (card, UPI, netbanking). This is processed via the payment gateway and
                typically reaches the customer in 5–7 business days.
              </li>
              <li>
                <strong>Wallet</strong> — The customer&apos;s in-app spendable balance. When a customer
                uses wallet to pay, that amount is reversed back to their wallet on cancellation.
              </li>
              <li>
                <strong>Cashback</strong> — Reward earned by the customer on a successful booking. It sits
                in their wallet&apos;s pending area for a period after travel before being released to
                their spendable balance.
              </li>
              <li>
                <strong>Held balance vs available balance</strong> — Held means pending cashback that has
                not been released yet. Available means spendable balance the customer can use. When we
                reverse cashback, we deduct from the held balance only — we never touch the spendable
                balance.
              </li>
              <li>
                <strong>Convenience fee</strong> — Our service charge added at booking time. It is
                non-refundable except in DGCA cases where the airline cancelled the flight.
              </li>
              <li>
                <strong>Promo code / promo discount</strong> — Discount applied at booking time. Promo
                discounts are not refunded as money. The promo code usage is reversed so the customer can
                use it again on a future booking.
              </li>
              <li>
                <strong>Gift card</strong> — Currently the gift card amount is not being deducted at booking
                time, so no reversal is performed on cancellation. This will change in the future.
              </li>
            </ul>
          </section>

          <section>
            <h3 className="text-base font-semibold text-gray-900">Cancellation and refund flow</h3>
            <ul className="mt-2 list-disc space-y-2 pl-5">
              <li>
                <strong>Cancellation charge</strong> — Amount the airline keeps when a booking is
                cancelled. Different airlines have different rules.
              </li>
              <li>
                <strong>Full cancellation</strong> — Cancelling the entire booking for all passengers.
              </li>
              <li>
                <strong>Partial cancellation</strong> — Cancelling only some passengers from a booking.
              </li>
              <li>
                <strong>DGCA rules</strong> — Indian aviation regulations. If a booking was made within 48
                hours of departure, the customer can cancel for free. If the airline cancels the flight, the
                customer gets a full refund of everything (including our convenience fee).
              </li>
              <li>
                <strong>Instant refund</strong> — A full cash refund issued when a booking failed (the
                customer paid but no flight ticket was generated).
              </li>
              <li>
                <strong>Manual refund</strong> — A refund initiated by an agent for a custom amount and
                reason, outside the standard cancellation flow.
              </li>
            </ul>
          </section>

          <section>
            <h3 className="text-base font-semibold text-gray-900">System terms</h3>
            <ul className="mt-2 list-disc space-y-2 pl-5">
              <li>
                <strong>Airline system</strong> — The third-party system we use to interact with airlines
                for booking, cancellation, and seat changes.
              </li>
              <li>
                <strong>Payment gateway</strong> — The third-party service that handles card, UPI, and
                netbanking payments and refunds.
              </li>
              <li>
                <strong>PNR</strong> — The booking reference number assigned by the airline.
              </li>
              <li>
                <strong>Booking ID</strong> — Our internal booking identifier (a UUID).
              </li>
              <li>
                <strong>Ticket ID</strong> — The support ticket number (for example, #100).
              </li>
            </ul>
          </section>

          <section>
            <h3 className="text-base font-semibold text-gray-900">Refund recovery</h3>
            <p className="mt-2">
              If a refund step fails (for example, the payment gateway is temporarily down), the ticket is
              automatically flagged for <strong>refund recovery</strong>. You will see:
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>A banner at the top of the ticket detail page.</li>
              <li>A badge on the tickets list showing how many tickets need recovery.</li>
              <li>A &quot;Retry Failed Refund&quot; button that retries the failed step.</li>
            </ul>
            <p className="mt-2">
              Always check the recovery banner before contacting technical support — most issues resolve
              with a retry.
            </p>
          </section>

          <section>
            <h3 className="text-base font-semibold text-gray-900">Changing a flight date (reschedule)</h3>
            <ul className="mt-2 list-disc space-y-2 pl-5">
              <li>
                <strong>Reschedule</strong>: the customer keeps the same booking but moves to a different
                flight or date. The booking reference (PNR) usually stays the same.
              </li>
              <li>
                <strong>Change fee</strong>: what the airline charges to move the customer to the new
                flight. You get it in step 2 with &quot;Get the fee from the airline&quot;.
              </li>
              <li>
                <strong>Price difference</strong>: the new flight&apos;s price minus the current flight&apos;s
                price. If the new flight costs more, the customer pays the difference plus the change fee.
              </li>
              <li>
                <strong>TBO</strong>: the company we book airline tickets through. When we &quot;change the
                flight&quot;, we ask TBO to change the ticket with the airline.
              </li>
              <li>
                <strong>&quot;We are not sure if the flight was changed&quot;</strong>: we asked TBO to change
                the flight but did not get a clear answer. Never change it again by hand. Click &quot;Check
                with the airline&quot;; only use &quot;Try the change again&quot; when the check says the flight
                was NOT changed.
              </li>
              <li>
                <strong>Fee given back</strong>: the customer paid from their credit line, the change could
                not be made, so the fee went back to their credit line automatically.
              </li>
            </ul>
          </section>

          <section>
            <h3 className="text-base font-semibold text-gray-900">What you can and cannot do</h3>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>Cancel a booking (full or partial)</li>
              <li>Process a manual refund with custom amount</li>
              <li>Issue an instant refund for a failed booking</li>
              <li>Reschedule a booking</li>
              <li>Add comments to a ticket</li>
              <li>Notify the customer</li>
              <li>Retry a failed refund</li>
              <li className="text-gray-600">
                Reassign a ticket to another agent — please contact your supervisor
              </li>
              <li className="text-gray-600">
                Edit the cancellation charge after a refund has been processed — refunds are final
              </li>
              <li className="text-gray-600">
                Manually mark a ticket as recovered — the system clears the flag automatically when the
                retry succeeds
              </li>
            </ul>
          </section>

          <section>
            <h3 className="text-base font-semibold text-gray-900">When to escalate to technical support</h3>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>The retry button keeps failing after 2–3 attempts</li>
              <li>The system shows error messages you do not understand</li>
              <li>
                The refund amount calculated by the system does not match what the customer says they
                paid
              </li>
              <li>Anything involving more than ₹50,000 — confirm with your supervisor first</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
